import React, { useState } from 'react';
import { toNumberOrMinusOne } from 'utils/helpers';
import { PERMISSIONS } from 'constants/permissions';
import { CalendarIcon } from 'components/common/icons';
import { ValidationErrors } from 'constants/validations';
import { useAccessControl } from 'components/common/accessControl/useAccessControl';
import { ContactPersonData } from 'components/common/contactPerson/contactPersonCard';
import { AttachmentForm } from 'feature/purchaseRequestTabs/supplierOffers/edit/attachments';
import { Col, DatePicker, Form, Input, Row, Select, SupplierSelect, Typography } from 'components';
import styles from './../styles.module.scss';

const { Text } = Typography;

interface IGeneralInformation {
  attachments: { type: string; file: File }[];
  setAttachments: (attachments: { type: string; file: File }[]) => void;
  handleFormItemBlur: (fieldKey: string, value: string | number) => void;
  handleFormItemChange: (fieldKey: string, value: string) => void;
  setOpenContactPersonModal: (value: boolean) => void;
  onAttachmentAdd: (attachments: { type: string; file: File }[]) => void;
  onAttachmentDelete: (ids: string[]) => void;
  isLoadingContactPerson: boolean;
  selectedSupplier?: string;
  contactPersons: ContactPersonData[];
  purchaseRequest: Record<string, any>;
}

export const GeneralInformation = React.memo((props: IGeneralInformation) => {
  const { hasAccess } = useAccessControl();
  const [fileList, setFileList] = useState<File[]>([]);
  const [attachmentType, setAttachmentType] = useState<string | undefined>();

  const {
    attachments,
    setAttachments,
    isLoadingContactPerson,
    contactPersons,
    selectedSupplier,
    onAttachmentAdd,
    onAttachmentDelete,
    handleFormItemBlur,
    handleFormItemChange,
    setOpenContactPersonModal,
    purchaseRequest,
  } = props;
  return (
    <Row style={{ padding: '20px' }} gutter={[0, 20]}>
      <Col span={24}>
        <Text style={{ color: '#05796B' }} className={'font-weight-800 font-size-16'}>
          General Information
        </Text>
      </Col>
      <Col span={24}>
        <Row gutter={[0, 32]}>
          <Col span={24}>
            <Row gutter={[12, 20]}>
              <Col span={12}>
                <Form.Item
                  name="supplierId"
                  rules={[
                    {
                      required: true,
                      message: ValidationErrors.required('Supplier'),
                    },
                  ]}
                  label={'Supplier*'}
                >
                  <SupplierSelect
                    disabled={purchaseRequest?.isFinalStatus}
                    onChange={(value) => handleFormItemChange('supplierId', value)}
                    size={'large'}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="source"
                  rules={[
                    {
                      required: true,
                      message: ValidationErrors.required('Source'),
                    },
                  ]}
                  label={'Source*'}
                >
                  <Select
                    disabled={purchaseRequest?.isFinalStatus}
                    onChange={(value) => handleFormItemChange('source', value)}
                    options={[
                      { value: 'EMAIL', label: 'Email' },
                      { value: 'PRICE_LIST', label: 'Price list' },
                    ]}
                    size={'large'}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="contactPersonId"
                  rules={[
                    {
                      required: true,
                      message: ValidationErrors.required('Contact Person'),
                    },
                  ]}
                  label={'Contact Person*'}
                >
                  <Select
                    disabled={purchaseRequest?.isFinalStatus || !selectedSupplier}
                    onChange={(value) => handleFormItemChange('contactPersonId', value)}
                    size={'large'}
                    loading={isLoadingContactPerson}
                    onAdd={() => setOpenContactPersonModal(true)}
                    showAdd={hasAccess(PERMISSIONS.clients.contact_person.write)}
                    options={contactPersons}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="offerNumber"
                  rules={[
                    {
                      required: true,
                      message: ValidationErrors.required('Supplier Offer Number'),
                    },
                  ]}
                  label={'Supplier Offer Number*'}
                >
                  <Input
                    disabled={purchaseRequest?.isFinalStatus}
                    onBlur={(e) => handleFormItemBlur('offerNumber', toNumberOrMinusOne(e.target.value))}
                    size={'large'}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="supplierOfferValidity"
                  label={'Offer Validity from Supplier*'}
                  rules={[
                    {
                      required: true,
                      message: ValidationErrors.required('Offer Validity from Supplier'),
                    },
                  ]}
                >
                  <DatePicker
                    disabled={purchaseRequest?.isFinalStatus}
                    onChange={(date) => handleFormItemChange('supplierOfferValidity', date.toISOString())}
                    style={{ width: '100%' }}
                    format="YYYY-MM-DD"
                    size={'large'}
                    suffixIcon={<CalendarIcon />}
                    popupClassName={'fmg-range-picker'}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="clientOfferValidity"
                  label={'Offer Validity for Client*'}
                  rules={[
                    {
                      required: true,
                      message: ValidationErrors.required('Offer Validity for Client'),
                    },
                  ]}
                >
                  <DatePicker
                    disabled={purchaseRequest?.isFinalStatus}
                    onChange={(date) => handleFormItemChange('clientOfferValidity', date.toISOString())}
                    style={{ width: '100%' }}
                    format="YYYY-MM-DD"
                    size={'large'}
                    suffixIcon={<CalendarIcon />}
                    popupClassName={'fmg-range-picker'}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col span={24} className={styles.attachments}>
            <AttachmentForm
              attachments={attachments}
              setAttachments={setAttachments}
              onAdd={onAttachmentAdd}
              onDelete={onAttachmentDelete}
              fileList={fileList}
              setFileList={setFileList}
              setAttachmentType={setAttachmentType}
              attachmentType={attachmentType}
              purchaseRequest={purchaseRequest}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
});
